import { getRootStore } from 'models/root';

const numberUtilities = {
  roundThresholds: num => {
    if (!num) return '0.0';
    const result = `${Number(num).toFixed(1)}.0`;

    return result.slice(0, result.indexOf('.') + 2);
  },

  celsiusToFahrenheit: (val, round = false, signsAmount = 0) => {
    if (!Number.isFinite(val)) return null;

    let result = val * (9 / 5) + 32;

    if (round && Number.isInteger(Math.abs(signsAmount))) {
      const multer = Math.pow(10, Math.abs(signsAmount));

      return Math.round((result + Number.EPSILON) * multer) / multer;
    }

    return result;
  },

  celsiusWrapper: (val, options) => {
    const { round = true, signsAmount = 0, showUnits = false, fallback = 'N/A' } = options || {};

    if (!Number.isFinite(val)) return fallback;

    const root = getRootStore();
    const isDefaultUnitPreference = root.establishmentStore.isDefaultUnitPreference;

    let result = val;
    let unit = '°C';

    if (isDefaultUnitPreference) {
      result = val * (9 / 5) + 32;
      unit = '°F';
    }

    if (round && Number.isInteger(signsAmount) && signsAmount >= 0) {
      const multer = Math.pow(10, signsAmount);

      result = Math.round((result + Number.EPSILON) * multer) / multer;
    }

    return showUnits ? `${result} ${unit}` : result;
  },

  celsiusWrapperRevert: (val, signsAmount = 1) => {
    if (!Number.isFinite(val)) return null;

    const root = getRootStore();
    const isDefaultUnitPreference = root.establishmentStore.isDefaultUnitPreference;

    let result = val;

    if (isDefaultUnitPreference) {
      result = (val - 32) * (5 / 9);
    }

    const multer = Math.pow(10, Math.abs(signsAmount));
    result = Math.round((result + Number.EPSILON) * multer) / multer;

    return result;
  },

  fahrenheitToCelsius: (val, signsAmount = 0) => {
    if (!Number.isFinite(val)) return null;

    let result = (val - 32) * (5 / 9);

    if (signsAmount && Number.isInteger(Math.abs(signsAmount))) {
      const multer = Math.pow(10, Math.abs(signsAmount));

      return Math.round((result + Number.EPSILON) * multer) / multer;
    }

    return result;
  },

  ftToMeters: val => val * 0.3048,
  metersToFt: val => val / 0.3048,

  convertVolumeName: () => {
    const root = getRootStore();
    const unit_system = root.establishmentStore.unitPreference;
    return unit_system === 'English' ? 'gaL' : 'L';
  },

  convertVolumeValue: value => {
    const root = getRootStore();
    const unit_system = root.establishmentStore.unitPreference;
    return (unit_system === 'English' ? value / 3.78541178 : value).toFixed(2);
  },

  mlToWeight: ml => {
    if (Number.isNaN(+ml)) return null;

    const kgCoefficient = 0.001;
    const lbCoefficient = 0.0022046;

    const root = getRootStore();

    const coefficient = root.establishmentStore.isDefaultUnitPreference
      ? lbCoefficient
      : kgCoefficient;

    return (+ml * coefficient).toFixed(2);
  },

  convertTemperature: value => {
    if (!value) return null;

    const root = getRootStore();
    const unit_system = root.establishmentStore.unitPreference;

    if (unit_system === 'English') {
      return (value * (9 / 5) + 32).toFixed(1);
    }
    return value.toFixed(1);
  },

  galToMl: val => val * 3785.411784,
  mlToGal: val => val / 3785.411784,
  convertFlowRate: value => {
    const root = getRootStore();
    const unit_system = root.establishmentStore.unitPreference;
    if (unit_system === 'English') {
      return value.toFixed(2);
    }
    return (value * 1.7744117738).toFixed(2);
  },
  roundToDecimal: (val, signsAmount = 2) => {
    if (!Number.isFinite(val)) return null;

    const multer = Math.pow(10, Math.abs(signsAmount));

    return Math.round((val + Number.EPSILON) * multer) / multer;
  },

  calcInitialTotal: (total_volume_ml, percentage) =>
    numberUtilities.roundToDecimal((total_volume_ml * percentage) / 100),

  // Pours are deducted from initial_volume_total, so only the unfilled part of the
  // keg (capacity - initial) is carried over. initial_volume_total is in the container's own unit.
  calcInitialTotalForNewSize: (item, newContainer) => {
    const mlPerUnit = item._containers_volume_total_ml / item._containers_volume_total;
    const newMlPerUnit = newContainer._volume_total_ml / newContainer.volume_total;
    const unfilledMl = item._containers_volume_total_ml - item.initial_volume_total * mlPerUnit;

    return numberUtilities.roundToDecimal(
      (newContainer._volume_total_ml - unfilledMl) / newMlPerUnit,
    );
  },

  calcInitialPercentage: (total_volume_ml, volume) =>
    numberUtilities.roundToDecimal((numberUtilities.galToMl(volume) / total_volume_ml) * 100),

  calcOnlineStatus: timestamp => (timestamp ? Math.round(timestamp / 60) < 5 : !!timestamp),

  toExponential: (num, signsAmount = 2) => {
    if (!Number.isFinite(num)) return null;

    const e = num.toExponential(signsAmount);

    return `${e.slice(0, e.indexOf('.') + signsAmount + 1)}${e.slice(e.indexOf('e'))}`;
  },

  toFormattedUSD: val => {
    const options = { style: 'currency', currency: 'USD' };
    const formatToUsd = new Intl.NumberFormat('en-US', options);
    return formatToUsd.format(val);
  },

  toFormattedCurrency: (val, currencyCode) => {
    const options = { style: 'currency', currency: currencyCode };
    const formatToUsd = new Intl.NumberFormat('en-US', options);
    return formatToUsd.format(val);
  },

  distanceBetweenPoints: (start, end) => {
    return Math.abs(Math.sqrt(Math.pow(start[0] - end[0], 2) + Math.pow(start[1] - end[1], 2)));
  },
};

export default numberUtilities;
